import { Typography } from '@/ui/components'
import * as Sentry from '@sentry/react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import styled from 'styled-components'

interface AppErrorBoundaryProps {
  fallback?: ReactNode
  children: ReactNode
}

interface AppErrorBoundaryState {
  hasError: boolean
}

const FallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 0 1.5rem;
  text-align: center;
`

const RetryButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  border: none;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.typography.variants['Button/Button-16-Semibold']};

  &:hover {
    opacity: 0.9;
  }
`

export default class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {
    hasError: false,
  }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled runtime error caught by boundary:', error, info)

    Sentry.captureException(error, {
      tags: {
        feature: 'error_boundary',
        flow: 'unhandled_runtime_error',
      },
      extra: {
        componentStack: info.componentStack,
      },
    })
  }

  private handleRetry = () => {
    this.setState({ hasError: false })
    window.location.reload()
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <FallbackContainer>
          <Typography variant="Heading/Heading-18-bold">問題が発生しました</Typography>
          <Typography variant="Body/Body-14-medium" color="textSecondary">
            画面を再読み込みしてください。それでも解決しない場合は管理者に連絡してください。
          </Typography>
          <RetryButton type="button" onClick={this.handleRetry}>
            再読み込み
          </RetryButton>
        </FallbackContainer>
      )
    }

    return this.props.children
  }
}
